import React from 'react';

import debounce from 'debounce';
import Supercluster from 'supercluster';

import { MAP_MAX_ZOOM_LEVEL } from '@kamernet/constants/Listings';
import { KamernetError } from '@kamernet/core/ApiClient';
import {
  ListingRequest,
  FindMapDataResponse,
  OpResponseCode,
  MapMarker,
} from '@kamernet/core/ApiClient/Kamernet';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { reportError } from '@kamernet/core/Errors';

import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';
import { ListingActionTypes } from '@kamernet/modules/Listing/context/types';

import { MapBounds } from '../pages/Listings/components/Map/types';
import { SearchView } from '../types';
import { adjustListingRequestForApiCall } from '../utilities/adjustListingRequestForApiCall';
import { groupRoomsByCoords } from '../utilities/groupRoomByCoords';

import { useListingStateDispatcher } from './useListingStateDispatcher';

export type PinListingSubset = {
  count: number;
};

const DEBOUNCE_MS = 1000;
interface State {
  pinListings: MapMarker[];
  clusterZoom: number;
}
export const useMapClusters = (
  bounds: MapBounds | undefined,
  zoom: number,
  searchOnMove: boolean,
) => {
  const { listingsState, dispatch } = ListingsContext.useContext();
  const { apiClient } = AppServicesContext.useContext();
  const [mapData, setMapData] = React.useState<MapMarker[]>();
  const { listingStateDispatcher } = useListingStateDispatcher();
  const [{ pinListings, clusterZoom }, setClusterState] = React.useState<State>(
    {
      pinListings: [],
      clusterZoom: zoom,
    },
  );

  const listings = React.useMemo(() => {
    const groupedPins = groupRoomsByCoords(
      pinListings.map(listing => ({
        type: 'Feature',
        properties: {
          count: listing.count,
          latitude: listing.latitude,
          Longitude: listing.Longitude,
        },
        geometry: {
          type: 'Point',
          count: listing.count,
          coordinates: [
            parseFloat(listing.Longitude.toString()),
            parseFloat(listing.latitude.toString()),
          ],
        },
      })),
    );
    let regularListings: typeof groupedPins = [];
    regularListings = groupedPins;

    const superclusterObj = new Supercluster<
      { rooms: MapMarker[]; cluster: false },
      { rooms: MapMarker[] }
    >({
      maxZoom: MAP_MAX_ZOOM_LEVEL - 1,
      radius: 80,
      map: props => {
        return {
          rooms: [...props.rooms],
          type: 'cluster',
        };
      },
      // eslint-disable-next-line no-return-assign
      reduce: (acc, props) => (acc.rooms = acc.rooms.concat(props.rooms)),
    });
    superclusterObj.load(regularListings);

    return { regularListingsCluster: superclusterObj };
  }, [pinListings]);

  const getClusterZoom = React.useCallback(
    (id: number) => listings.regularListingsCluster.getClusterExpansionZoom(id),
    [listings],
  );

  const clusters = React.useMemo(() => {
    if (!bounds) {
      return [];
    }

    const allClusters = listings.regularListingsCluster.getClusters(
      [bounds?.nw.lng, bounds?.se.lat, bounds?.se.lng, bounds?.nw.lat],
      // on mobile devices we might get not an integer and it will cause failure of clustering,
      Math.max(0, Math.floor(clusterZoom)),
    );

    return allClusters;
  }, [bounds, clusterZoom, listings]);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const requestIdRef = React.useRef<number | undefined>(undefined);
  const debouncedSearchListings = React.useMemo(
    () =>
      debounce(async (currentZoom: number) => {
        dispatch({
          type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
          payload: true,
        });
        const currentRequestId = Date.now();
        requestIdRef.current = currentRequestId;
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        await apiClient.listing
          .findMapData(
            {
              listingRequest: adjustListingRequestForApiCall({
                ...listingsState.filterResult.filters,
                lat_lo: bounds?.se.lat,
                lng_lo: bounds?.nw.lng,
                lat_hi: bounds?.nw.lat,
                lng_hi: bounds?.se.lng,
              }),
            },
            {
              signal: abortController.signal,
            },
          )
          .then((response: FindMapDataResponse) => {
            if (
              response &&
              response.OpResponse.Code === OpResponseCode.OPERATION_SUCCESSFUL
            ) {
              if (currentRequestId !== requestIdRef.current) {
                return;
              }
              setMapData(response.mapdata);
              setClusterState({
                pinListings: response.mapdata,
                clusterZoom: currentZoom,
              });
              dispatch({
                type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
                payload: false,
              });
            }
          })
          .catch((error: KamernetError) => {
            if (error && error.name.toLowerCase() !== 'fetcherror') {
              reportError(error as Error, {
                extra: {
                  context: 'Updating map cluster markers failed',
                  metaData: { error },
                },
              });
              dispatch({
                type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
                payload: false,
              });
            }
          });
      }, DEBOUNCE_MS),
    [
      apiClient.listing,
      bounds?.nw.lat,
      bounds?.nw.lng,
      bounds?.se.lat,
      bounds?.se.lng,
      dispatch,
      listingsState.filterResult.filters,
    ],
  );
  React.useEffect(() => {
    if (!bounds) {
      return;
    }

    debouncedSearchListings(zoom);
    const dispatchMapTiles = () => {
      listingStateDispatcher({
        ...listingsState.filterResult.filters,
        lat_lo: bounds?.se.lat,
        lng_lo: bounds?.nw.lng,
        lat_hi: bounds?.nw.lat,
        lng_hi: bounds?.se.lng,
        searchview: SearchView.MapTiles,
      } as ListingRequest);
    };
    if (searchOnMove) {
      dispatchMapTiles();
    }
  }, [
    debouncedSearchListings,
    bounds,
    zoom,
    listingStateDispatcher,
    listingsState.filterResult.filters,
    dispatch,
    searchOnMove,
  ]);
  return { clusters, getClusterZoom, mapData };
};
